import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

interface Company {
  id: string;
  name: string;
  color?: string;
}

interface UserCompaniesContextType {
  userCompanies: Company[];
  getCompanyName: (companyId: string) => string;
  getCompanyColor: (companyId: string) => string | undefined;
}

const UserCompaniesContext = createContext<UserCompaniesContextType | null>(null);

export const useUserCompanies = () => {
  const context = useContext(UserCompaniesContext);
  if (!context) {
    throw new Error('useUserCompanies must be used within UserCompaniesProvider');
  }
  return context;
};

interface UserCompaniesProviderProps {
  children: React.ReactNode;
  initialCompanies?: Company[];
}

export const UserCompaniesProvider: React.FC<UserCompaniesProviderProps> = ({
  children,
  initialCompanies = []
}) => {
  // Initialize from props (passed from shell via Module Federation)
  const [userCompanies, setUserCompanies] = useState<Company[]>(initialCompanies);

  // Sync state when initialCompanies prop changes (e.g., when shell finishes loading)
  // Compare by stringified IDs to avoid unnecessary updates from reference changes
  const initialCompanyIds = initialCompanies.map(c => c.id).join(',');
  useEffect(() => {
    if (initialCompanies.length > 0) {
      setUserCompanies(prev => {
        const prevIds = prev.map(c => c.id).sort().join(',');
        const nextIds = initialCompanies.map(c => c.id).sort().join(',');
        return prevIds === nextIds ? prev : initialCompanies;
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCompanyIds]);

  useEffect(() => {
    const handleUpdate = (event: CustomEvent<Company[]>) => {
      const incoming = event.detail;
      setUserCompanies(prev => {
        const prevIds = prev.map(c => c.id).sort().join(',');
        const nextIds = incoming.map(c => c.id).sort().join(',');
        return prevIds === nextIds ? prev : incoming;
      });
    };

    // Listen for updates from shell app (e.g., company switch)
    window.addEventListener('user-companies-updated', handleUpdate as EventListener);

    return () => {
      window.removeEventListener('user-companies-updated', handleUpdate as EventListener);
    };
  }, []);

  const getCompanyName = useCallback((companyId: string): string => {
    return userCompanies.find(c => c.id === companyId)?.name || '';
  }, [userCompanies]);

  const getCompanyColor = useCallback((companyId: string): string | undefined => {
    return userCompanies.find(c => c.id === companyId)?.color;
  }, [userCompanies]);

  const value = useMemo(() => ({
    userCompanies, getCompanyName, getCompanyColor,
  }), [userCompanies, getCompanyName, getCompanyColor]);

  return (
    <UserCompaniesContext.Provider value={value}>
      {children}
    </UserCompaniesContext.Provider>
  );
};
